import { useEffect, useState } from "react";
import { ClipboardList, Calendar, Loader2, Stethoscope, FlaskConical, Radiation, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { bookingApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";

const STATUS_STYLE = (s) => {
  const v = (s || "").toLowerCase();
  if (["confirmed", "accepted"].includes(v)) return "bg-emerald-100 text-emerald-700";
  if (["pending", "requested"].includes(v)) return "bg-amber-100 text-amber-700";
  if (["scheduled", "in_progress", "sample_collected", "scan_done", "in_reporting", "ongoing"].includes(v)) return "bg-sky-100 text-sky-700";
  if (["completed", "reported"].includes(v)) return "bg-violet-100 text-violet-700";
  if (["cancelled", "rejected", "declined"].includes(v)) return "bg-red-100 text-red-700";
  return "bg-muted text-muted-foreground";
};

function normalize(d) {
  const out = [];
  (d?.doctorBookings || []).forEach((b) => out.push({
    id: b.id, Icon: Stethoscope, color: "#2563EB",
    title: b.doctorName ? `Dr. ${String(b.doctorName).replace(/^Dr\.?\s*/i, "")}` : "Doctor visit",
    sub: b.specialty || (b.consultationType || "").replace(/_/g, " ") || "Consultation",
    date: b.date, ref: b.referenceNumber, status: b.status,
  }));
  (d?.labBookings || []).forEach((b) => out.push({
    id: b.id, Icon: FlaskConical, color: "#16A34A",
    title: b.labName || "Lab test", sub: `${(b.tests || []).length || 0} test(s)`,
    date: b.date, ref: b.referenceNumber, status: b.status,
  }));
  (d?.diagnosticBookings || []).forEach((b) => out.push({
    id: b.id, Icon: Radiation, color: "#DB2777",
    title: b.centreName || "Diagnostic centre",
    sub: (b.scans || []).map((s) => s.modality).filter(Boolean).join(", ") || "Diagnostic scan",
    date: b.date, ref: b.referenceNumber, status: b.status,
  }));
  (d?.serviceBookings || []).forEach((b) => out.push({
    id: b.id, Icon: Building2, color: "#7C3AED",
    title: b.serviceName || "Hospital service", sub: b.hospitalName || "Hospital",
    date: b.date, ref: b.referenceNumber, status: b.status,
  }));
  return out.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function BookingsReports() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?._id) return;
    bookingApi
      .all(user._id)
      .then((res) => setItems(normalize(res.data)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [user?._id]);

  return (
    <div className="max-w-3xl" data-testid="bookings-reports-page">
      <PageHeader title="Bookings & Reports" subtitle="All your bookings in one place" icon={ClipboardList} color="#16A34A" />
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-3xl p-8 text-center border-border">
          <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-3 font-semibold">No bookings yet</p>
          <p className="text-sm text-muted-foreground mt-1">Your doctor, lab, diagnostic and service bookings will show here.</p>
          <div className="flex gap-2 justify-center mt-4">
            <Button className="rounded-2xl" onClick={() => navigate("/doctors")} data-testid="bookings-find-doctor">Find a Doctor</Button>
            <Button variant="outline" className="rounded-2xl" onClick={() => navigate("/diagnostic-centres")} data-testid="bookings-diagnostics">Book Diagnostic</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((b, i) => (
            <Card key={b.id || i} className="rounded-2xl p-4 border-border flex items-center gap-3" data-testid={`booking-item-${i}`}>
              <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: b.color + "1A" }}>
                <b.Icon className="h-5 w-5" style={{ color: b.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{b.title}</p>
                <p className="text-sm text-muted-foreground truncate">{b.sub}</p>
                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                  {b.date && <Calendar className="h-3.5 w-3.5" />}
                  {b.date ? fmtDate(b.date) : (b.ref ? `Ref: ${b.ref}` : "")}
                </p>
              </div>
              {b.status && <span className={`text-[10px] font-bold px-2 py-1 rounded-full capitalize ${STATUS_STYLE(b.status)}`}>{String(b.status).replace(/_/g, " ")}</span>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
